import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip,
  CartesianGrid, BarChart, Bar, ReferenceLine, Legend,
} from 'recharts';
import SharedNav from '@/components/SharedNav';
import { API } from '@/App';
import {
  ArrowLeft, History, MapPin, Loader2, AlertTriangle,
  Wheat, Database, FlaskConical, Droplets,
} from 'lucide-react';
import ConfidenceChip from '@/components/predict/ConfidenceChip';
import DataSourceLine from '@/components/predict/DataSourceLine';
import MethodologyLink from '@/components/predict/MethodologyLink';
import UncertaintyBand from '@/components/predict/UncertaintyBand';
import ValidationStatus from '@/components/predict/ValidationStatus';
import DisclaimerBanner from '@/components/predict/DisclaimerBanner';

/**
 * /predict/historical-yield — Sprint 1 Day 4-5 + Day 6-7.
 *
 * Day 4-5: District-level granularity with auto-populated district selector
 *          and accurate NASA POWER coords per district.
 * Day 6-7: Drought-year anomaly markers on the yield chart, classified via
 *          NASA POWER JJAS rainfall vs climatology (IMD-style ±25 % rule).
 */

const SAGE = '#2D6A4F';
const SAGE_DARK = '#1B4332';
const PROVISIONAL_AMBER = '#92400E';
const DROUGHT_RED = '#C62828';
const WET_BLUE = '#1565C0';
const ALL_DISTRICTS = '__ALL__';

const CLASSIFICATION_COLOR = {
  drought: DROUGHT_RED,
  wet: WET_BLUE,
  normal: SAGE_DARK,
  incomplete: '#999999',
};

export default function HistoricalYield() {
  const navigate = useNavigate();
  const [catalogue, setCatalogue] = useState(null);
  const [crop, setCrop] = useState('pearl_millet');
  const [state, setState] = useState('Rajasthan');
  const [district, setDistrict] = useState(ALL_DISTRICTS);
  const [yearStart, setYearStart] = useState(2018);
  const [yearEnd, setYearEnd] = useState(2022);
  const [yieldData, setYieldData] = useState(null);
  const [climatology, setClimatology] = useState(null);
  const [monsoonData, setMonsoonData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [climLoading, setClimLoading] = useState(false);
  const [monsoonLoading, setMonsoonLoading] = useState(false);
  const [error, setError] = useState(null);

  // Catalogue once. `API` is a stable module-level constant — listed
  // in deps to satisfy react-hooks/exhaustive-deps without runtime cost.
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/predict/historical/catalogue`)
      .then(r => { if (alive) setCatalogue(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || 'Catalogue load failed.'); });
    return () => { alive = false; };
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps -- API is module-level constant

  // When state changes, reset district to "all"
  useEffect(() => {
    setDistrict(ALL_DISTRICTS);
  }, [state]);

  // Districts available for current state
  const stateDistricts = useMemo(() => {
    if (!catalogue?.districts_per_state) return [];
    return catalogue.districts_per_state[state] || [];
  }, [catalogue, state]);

  // Resolve effective coords: district > state default
  const effectiveCoords = useMemo(() => {
    if (!catalogue) return null;
    if (district !== ALL_DISTRICTS) {
      const key = `${state}|${district}`;
      const c = catalogue.district_coords?.[key];
      if (c) return { lat: c.lat, lon: c.lon, place: district };
    }
    const s = catalogue.states?.find(x => x.name === state);
    if (s?.lat != null) return { lat: s.lat, lon: s.lon, place: s.place };
    return null;
  }, [catalogue, state, district]);

  // Fetch yield + climatology + monsoon when controls change
  useEffect(() => {
    if (!catalogue) return;
    let alive = true;
    setLoading(true);
    setError(null);
    const yieldParams = { state, crop, year_start: yearStart, year_end: yearEnd };
    if (district !== ALL_DISTRICTS) yieldParams.district = district;
    axios.get(`${API}/predict/historical/yield`, { params: yieldParams })
      .then(r => { if (alive) setYieldData(r.data); })
      .catch(e => { if (alive) setError(e?.response?.data?.detail || 'Yield query failed.'); })
      .finally(() => { if (alive) setLoading(false); });

    if (effectiveCoords) {
      setClimLoading(true);
      axios.get(`${API}/predict/historical/climatology`, {
        params: { lat: effectiveCoords.lat, lon: effectiveCoords.lon },
      })
        .then(r => { if (alive) setClimatology(r.data); })
        .catch(() => { /* non-fatal */ })
        .finally(() => { if (alive) setClimLoading(false); });

      setMonsoonLoading(true);
      axios.get(`${API}/predict/historical/monsoon-rainfall`, {
        params: { lat: effectiveCoords.lat, lon: effectiveCoords.lon, year_start: yearStart, year_end: yearEnd },
      })
        .then(r => { if (alive) setMonsoonData(r.data); })
        .catch(() => { if (alive) setMonsoonData(null); })
        .finally(() => { if (alive) setMonsoonLoading(false); });
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- API is a module-level constant; setters are stable
  }, [catalogue, state, district, crop, yearStart, yearEnd, effectiveCoords]);

  // Yield records enriched with monsoon classification per year
  const yieldEnriched = useMemo(() => {
    if (!yieldData?.records?.length) return [];
    const classMap = {};
    const rainMap = {};
    if (monsoonData?.yearly) {
      monsoonData.yearly.forEach(y => {
        classMap[y.year] = y.classification;
        rainMap[y.year] = y.monsoon_rainfall_mm;
      });
    }
    return yieldData.records.map(r => ({
      ...r,
      classification: classMap[r.year] || 'unknown',
      monsoon_rainfall_mm: rainMap[r.year],
    }));
  }, [yieldData, monsoonData]);

  const yieldStats = useMemo(() => {
    if (!yieldEnriched.length) return null;
    const values = yieldEnriched.map(r => r.yield_kg_per_ha);
    const sorted = [...values].sort((a, b) => a - b);
    const median = sorted[Math.floor(sorted.length / 2)];
    const q1 = sorted[Math.floor(sorted.length * 0.25)];
    const q3 = sorted[Math.floor(sorted.length * 0.75)];
    const min = sorted[0];
    const max = sorted[sorted.length - 1];
    const mean = values.reduce((a, b) => a + b, 0) / values.length;
    const droughtYears = yieldEnriched.filter(r => r.classification === 'drought').length;
    const wetYears = yieldEnriched.filter(r => r.classification === 'wet').length;
    return { median, q1, q3, min, max, mean: Math.round(mean), n: values.length, droughtYears, wetYears };
  }, [yieldEnriched]);

  const cropLabel = catalogue?.crops_in_scope_full?.find(c => c.key === crop)?.label || crop;

  return (
    <div style={{ background: 'var(--page-bg)', minHeight: '100vh' }}>
      <SharedNav />

      <main className="max-w-[1180px] mx-auto px-6 pt-10 pb-24" data-testid="historical-yield-page">
        <button
          onClick={() => navigate('/predict')}
          className="inline-flex items-center gap-1.5 mb-6 transition-colors"
          style={{ fontSize: 13, color: '#6F6A63', background: 'transparent', border: 'none', cursor: 'pointer' }}
          onMouseEnter={(e) => { e.currentTarget.style.color = SAGE; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = '#6F6A63'; }}
          data-testid="historical-yield-back"
        >
          <ArrowLeft size={14} /> Back to Predict
        </button>

        <header className="mb-8" data-testid="historical-yield-header">
          <div className="flex items-center gap-2 mb-3 flex-wrap">
            <span style={{ background: '#E6F4EA', color: '#1B5E20', border: '1px solid #A5D6A7', borderRadius: 999, padding: '4px 12px', fontSize: 11.5, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase' }}>
              Tier A · ready to build
            </span>
            <span className="font-body" style={{ fontSize: 11.5, color: '#8A8580', textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 600 }}>
              Sprint 1 Day 4-7 · district + drought
            </span>
            <ValidationStatus status="pilot-pending" />
          </div>
          <h1 className="font-display" style={{ fontSize: 38, fontWeight: 400, color: SAGE_DARK, letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: 6 }}>
            Historical Yield Explorer
          </h1>
          <p className="font-display" style={{ fontSize: 16, color: '#6F6A63', fontStyle: 'italic' }}>
            What happened, where, and when — with monsoon-driven drought years called out using NASA POWER + IMD-style ±25 % rule.
          </p>
        </header>

        <DisclaimerBanner module="historical-yield" />

        {yieldData?.all_provisional && (
          <div
            className="rounded-xl p-4 flex items-start gap-3 mb-5"
            style={{ background: '#FFFBF1', border: '1px solid #F5E5B8' }}
            data-testid="historical-yield-provisional-banner"
          >
            <AlertTriangle size={18} style={{ color: PROVISIONAL_AMBER, marginTop: 2, flexShrink: 0 }} />
            <div>
              <div className="font-body" style={{ fontSize: 13, fontWeight: 700, color: PROVISIONAL_AMBER, marginBottom: 4 }}>
                PROVISIONAL DATASET — Sprint 1 seed
              </div>
              <p className="font-body" style={{ fontSize: 12.5, color: '#6B5500', lineHeight: 1.6 }}>
                Yield records below are provisional template figures. Awaiting validation by Dr. Praveen Kumar
                (Agriculture University Jodhpur) against agricoop.nic.in <em>Agricultural Statistics at a Glance 2023</em>.
                Do not cite as fact.
              </p>
            </div>
          </div>
        )}

        {/* Controls — now 5 columns when state has districts */}
        <section
          className="rounded-2xl p-5 mb-7 grid grid-cols-1 md:grid-cols-5 gap-4"
          style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }}
          data-testid="historical-yield-controls"
        >
          <Selector label="State" testid="control-state">
            <select
              value={state}
              onChange={e => setState(e.target.value)}
              data-testid="control-state-select"
              className="w-full rounded-lg px-3 py-2 font-body"
              style={selectStyle}
            >
              {catalogue?.states?.map(s => (
                <option key={s.name} value={s.name}>{s.name}</option>
              ))}
            </select>
          </Selector>
          <Selector label="District" testid="control-district">
            <select
              value={district}
              onChange={e => setDistrict(e.target.value)}
              data-testid="control-district-select"
              disabled={stateDistricts.length === 0}
              className="w-full rounded-lg px-3 py-2 font-body"
              style={{
                ...selectStyle,
                opacity: stateDistricts.length === 0 ? 0.55 : 1,
                cursor: stateDistricts.length === 0 ? 'not-allowed' : 'pointer',
              }}
              title={stateDistricts.length === 0 ? 'No district-level data on file for this state' : ''}
            >
              <option value={ALL_DISTRICTS}>{stateDistricts.length === 0 ? 'State-level only' : 'All districts (state-level)'}</option>
              {stateDistricts.map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </Selector>
          <Selector label="Crop" testid="control-crop">
            <select
              value={crop}
              onChange={e => setCrop(e.target.value)}
              data-testid="control-crop-select"
              className="w-full rounded-lg px-3 py-2 font-body"
              style={selectStyle}
            >
              {catalogue?.crops?.map(c => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </Selector>
          <Selector label="From year" testid="control-year-start">
            <input
              type="number" min={2000} max={2024}
              value={yearStart}
              onChange={e => setYearStart(parseInt(e.target.value, 10) || 2010)}
              data-testid="control-year-start-input"
              className="w-full rounded-lg px-3 py-2 font-body"
              style={selectStyle}
            />
          </Selector>
          <Selector label="To year" testid="control-year-end">
            <input
              type="number" min={2000} max={2024}
              value={yearEnd}
              onChange={e => setYearEnd(parseInt(e.target.value, 10) || 2022)}
              data-testid="control-year-end-input"
              className="w-full rounded-lg px-3 py-2 font-body"
              style={selectStyle}
            />
          </Selector>
        </section>

        {error && (
          <div className="rounded-xl p-4 mb-5" style={{ background: '#FBE9E3', border: '1px solid #C87A5E', color: '#8A2B1A' }} data-testid="historical-yield-error">
            <p className="font-body" style={{ fontSize: 13.5 }}><strong>Error:</strong> {error}</p>
          </div>
        )}

        {yieldStats && (
          <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-7" data-testid="historical-yield-summary">
            <SummaryCard
              icon={Wheat}
              title="Median yield"
              testid="summary-median"
              value={
                <UncertaintyBand
                  value={yieldStats.median}
                  unit="kg/ha"
                  kind="iqr"
                  lower={yieldStats.q1}
                  upper={yieldStats.q3}
                  size="lg"
                />
              }
              footnote={`${yieldStats.n} years · IQR ${yieldStats.q1}–${yieldStats.q3}`}
            />
            <SummaryCard
              icon={History}
              title="Range observed"
              testid="summary-range"
              value={
                <UncertaintyBand
                  value={yieldStats.mean}
                  unit="kg/ha"
                  kind="range"
                  lower={yieldStats.min}
                  upper={yieldStats.max}
                  size="lg"
                />
              }
              footnote={`Min ${yieldStats.min}, max ${yieldStats.max}`}
            />
            <SummaryCard
              icon={Droplets}
              title="Monsoon classification"
              testid="summary-monsoon"
              value={
                monsoonLoading ? (
                  <span style={{ fontSize: 13, color: '#8A8580' }}>NASA POWER…</span>
                ) : monsoonData ? (
                  <UncertaintyBand
                    value={`${yieldStats.droughtYears} drought · ${yieldStats.wetYears} wet`}
                    kind="class"
                    size="lg"
                  />
                ) : (
                  <span style={{ fontSize: 13, color: '#8A8580' }}>—</span>
                )
              }
              footnote={
                monsoonData
                  ? `JJAS @ ${effectiveCoords?.place} · climatology ${monsoonData.climatology_jjas_mm} mm`
                  : 'JJAS rainfall classification'
              }
            />
          </section>
        )}

        {/* Charts */}
        <section className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-7" data-testid="historical-yield-charts">
          <ChartCard
            title="Yield time-series · drought-marked"
            subtitle={`${cropLabel} · ${district !== ALL_DISTRICTS ? `${district}, ` : ''}${state} · ${yearStart}–${yearEnd}`}
            testid="chart-yield-timeseries"
            confidence="moderate"
            confidenceReason="Provisional yield seed; drought classification from NASA POWER (high confidence)."
            extraLegend={<DroughtLegend />}
          >
            {loading && <CenteredSpinner />}
            {!loading && yieldEnriched.length ? (
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={yieldEnriched} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="#EFF1EA" strokeDasharray="3 4" />
                  <XAxis dataKey="year" stroke="#6F6A63" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6F6A63" tick={{ fontSize: 11 }} label={{ value: 'kg/ha', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6F6A63' } }} />
                  <Tooltip content={<ClimateTooltip />} />
                  {yieldStats && <ReferenceLine y={yieldStats.median} stroke={SAGE} strokeDasharray="4 4" label={{ value: `Median ${yieldStats.median}`, fontSize: 10, fill: SAGE, position: 'right' }} />}
                  <Line
                    type="monotone"
                    dataKey="yield_kg_per_ha"
                    stroke={SAGE_DARK}
                    strokeWidth={2.2}
                    dot={renderClimateDot}
                    activeDot={{ r: 6 }}
                    name="yield (kg/ha)"
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : !loading && (
              <EmptyChart message="No yield data for this filter combination." />
            )}
            <div className="mt-3">
              <DataSourceLine
                sources={[
                  { name: 'agricoop.nic.in (PROVISIONAL seed)' },
                  { name: 'NASA POWER · drought classification', url: 'https://power.larc.nasa.gov/' },
                ]}
                lastUpdated={null}
              />
            </div>
          </ChartCard>

          <ChartCard
            title="Monthly rainfall climatology"
            subtitle={`NASA POWER · ${effectiveCoords?.place || state}`}
            testid="chart-rainfall-climatology"
            confidence="high"
            confidenceReason="NASA POWER multi-decade means; validated for India by Bandyopadhyay et al. (2020)."
          >
            {climLoading && <CenteredSpinner />}
            {!climLoading && climatology?.rainfall_summary?.monthly_rainfall ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={climatology.rainfall_summary.monthly_rainfall} margin={{ top: 10, right: 16, left: 0, bottom: 8 }}>
                  <CartesianGrid stroke="#EFF1EA" strokeDasharray="3 4" />
                  <XAxis dataKey="month" stroke="#6F6A63" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#6F6A63" tick={{ fontSize: 11 }} label={{ value: 'mm', angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: '#6F6A63' } }} />
                  <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} formatter={(v) => `${v} mm`} />
                  <Bar dataKey="rainfall_mm" fill={SAGE} radius={[4, 4, 0, 0]} name="rainfall" />
                  <Legend wrapperStyle={{ fontSize: 11, paddingTop: 6 }} />
                </BarChart>
              </ResponsiveContainer>
            ) : !climLoading && (
              <EmptyChart message="NASA POWER climatology unavailable. Try again later." />
            )}
            <div className="mt-3">
              <DataSourceLine
                sources={[
                  { name: 'NASA POWER · Climatology', url: 'https://power.larc.nasa.gov/' },
                ]}
                lastUpdated={null}
              />
            </div>
          </ChartCard>
        </section>

        {/* Monsoon detail card if data present */}
        {monsoonData?.yearly?.length > 0 && (
          <section
            className="rounded-2xl p-5 mb-7"
            style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }}
            data-testid="monsoon-detail-card"
          >
            <div className="flex items-baseline justify-between flex-wrap gap-3 mb-3">
              <div>
                <h3 className="font-display" style={{ fontSize: 17, color: SAGE_DARK, fontWeight: 500 }}>
                  Monsoon (JJAS) rainfall — IMD-style classification
                </h3>
                <p className="font-body" style={{ fontSize: 12, color: '#8A8580', fontStyle: 'italic' }}>
                  Classification rule: drought = JJAS &lt; 75 % climatology · wet = &gt; 125 % · normal = within band.
                </p>
              </div>
              <ConfidenceChip
                level="high"
                reason="NASA POWER daily PRECTOTCORR vs multi-decade climatology, IMD-style ±25 % thresholds."
                compact
              />
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2.5 mt-4">
              {monsoonData.yearly.map(y => (
                <div
                  key={y.year}
                  className="rounded-lg p-3"
                  style={{
                    background: '#FAFBF9',
                    border: `1px solid ${y.classification === 'drought' ? '#E8B5B5' : y.classification === 'wet' ? '#B5C9E8' : '#E5E7DD'}`,
                  }}
                  data-testid={`monsoon-year-${y.year}`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="font-display" style={{ fontSize: 15, color: SAGE_DARK, fontWeight: 600 }}>
                      {y.year}
                    </span>
                    <span
                      style={{
                        fontSize: 9.5,
                        fontWeight: 700,
                        color: CLASSIFICATION_COLOR[y.classification] || SAGE_DARK,
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                      }}
                    >
                      {y.classification}
                    </span>
                  </div>
                  <div className="font-body" style={{ fontSize: 14, color: SAGE_DARK, fontWeight: 600 }}>
                    {y.monsoon_rainfall_mm} mm
                  </div>
                  <div className="font-body" style={{ fontSize: 11, color: y.anomaly_pct < 0 ? DROUGHT_RED : y.anomaly_pct > 0 ? WET_BLUE : '#6F6A63' }}>
                    {y.anomaly_pct >= 0 ? '+' : ''}{y.anomaly_pct}% vs climatology
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <DataSourceLine
                sources={[{ name: 'NASA POWER · daily PRECTOTCORR', url: 'https://power.larc.nasa.gov/' }]}
                lastUpdated={null}
              />
            </div>
          </section>
        )}

        <section className="flex flex-wrap items-center justify-between gap-3 pt-6" style={{ borderTop: '1px solid #E5E7DD' }} data-testid="historical-yield-footer">
          <div className="flex items-center gap-2 flex-wrap">
            <FlaskConical size={14} style={{ color: SAGE }} />
            <span className="font-body" style={{ fontSize: 12, color: '#6F6A63', fontStyle: 'italic' }}>
              Sprint 1 Day 4-7 · districts + drought-year markers live · seed PROVISIONAL · IMD + AGMARKNET pending.
            </span>
          </div>
          <div className="flex items-center gap-2">
            <MethodologyLink module="historical-yield" />
            <button
              onClick={() => navigate('/predict/validation')}
              className="inline-flex items-center gap-1"
              style={{ fontSize: 11.5, color: SAGE, background: '#EAF2E5', border: '1px solid #C8DBC0', padding: '3px 7px', borderRadius: 6, fontWeight: 500, cursor: 'pointer' }}
              data-testid="open-validation-from-historical"
            >
              <Database size={11} />
              Validation Dashboard
            </button>
          </div>
        </section>
      </main>
    </div>
  );
}

const selectStyle = {
  fontSize: 13.5,
  color: SAGE_DARK,
  border: '1px solid #D8DBD0',
  background: '#FAFBF9',
};

function Selector({ label, testid, children }) {
  return (
    <div data-testid={testid}>
      <label className="font-body block mb-1.5" style={{ fontSize: 11, color: '#6F6A63', textTransform: 'uppercase', letterSpacing: '0.08em', fontWeight: 600 }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function SummaryCard({ icon: Icon, title, value, footnote, testid }) {
  return (
    <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }} data-testid={testid}>
      <div className="flex items-center gap-2 mb-3">
        <Icon size={15} style={{ color: SAGE }} />
        <span className="font-body" style={{ fontSize: 11, color: SAGE, textTransform: 'uppercase', letterSpacing: '0.1em', fontWeight: 700 }}>
          {title}
        </span>
      </div>
      <div className="mb-2">{value}</div>
      <div className="font-body" style={{ fontSize: 11.5, color: '#8A8580', lineHeight: 1.5 }}>
        {footnote}
      </div>
    </div>
  );
}

function ChartCard({ title, subtitle, testid, confidence, confidenceReason, extraLegend, children }) {
  return (
    <div className="rounded-2xl p-5" style={{ background: '#FFFFFF', border: '1px solid #E5E7DD' }} data-testid={testid}>
      <div className="flex items-start justify-between mb-3 gap-3 flex-wrap">
        <div>
          <h3 className="font-display" style={{ fontSize: 17, color: SAGE_DARK, fontWeight: 500, marginBottom: 2 }}>
            {title}
          </h3>
          <p className="font-body" style={{ fontSize: 12, color: '#8A8580', fontStyle: 'italic' }}>
            {subtitle}
          </p>
        </div>
        {confidence && <ConfidenceChip level={confidence} reason={confidenceReason} compact />}
      </div>
      {children}
      {extraLegend && <div className="mt-3">{extraLegend}</div>}
    </div>
  );
}

function CenteredSpinner() {
  return (
    <div className="flex items-center justify-center" style={{ height: 260 }}>
      <Loader2 size={22} className="animate-spin" style={{ color: SAGE }} />
    </div>
  );
}

function EmptyChart({ message }) {
  return (
    <div className="flex items-center justify-center text-center" style={{ height: 260 }}>
      <p className="font-body" style={{ fontSize: 12.5, color: '#8A8580', maxWidth: 280 }}>{message}</p>
    </div>
  );
}

// Recharts custom dot — color-codes each yield year by monsoon classification
function renderClimateDot(props) {
  const { cx, cy, payload } = props;
  if (typeof cx !== 'number' || typeof cy !== 'number') return null;
  const cls = payload?.classification;
  const fill = CLASSIFICATION_COLOR[cls] || SAGE_DARK;
  const r = cls === 'drought' || cls === 'wet' ? 5 : 3.5;
  return (
    <g>
      <circle cx={cx} cy={cy} r={r} fill={fill} stroke="#FFFFFF" strokeWidth={1.5} />
      {cls === 'drought' && (
        <circle cx={cx} cy={cy} r={r + 3} fill="none" stroke={DROUGHT_RED} strokeWidth={1} strokeDasharray="2 2" />
      )}
    </g>
  );
}

function ClimateTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const p = payload[0]?.payload || {};
  const cls = p.classification || 'unknown';
  const clsColor = CLASSIFICATION_COLOR[cls] || SAGE_DARK;
  return (
    <div
      style={{
        background: '#FFFFFF',
        border: '1px solid #E5E7DD',
        borderRadius: 8,
        padding: 10,
        fontSize: 12,
        boxShadow: '0 4px 10px rgba(20,30,15,0.08)',
      }}
    >
      <div style={{ fontWeight: 700, color: SAGE_DARK, marginBottom: 4 }}>{p.year}</div>
      <div style={{ color: SAGE_DARK }}>{p.yield_kg_per_ha} kg/ha</div>
      {p.monsoon_rainfall_mm != null && (
        <div style={{ color: '#6F6A63', marginTop: 3 }}>
          JJAS rainfall: <span style={{ color: clsColor, fontWeight: 600 }}>{p.monsoon_rainfall_mm} mm ({cls})</span>
        </div>
      )}
    </div>
  );
}

function DroughtLegend() {
  return (
    <div className="flex items-center justify-center gap-4 flex-wrap" data-testid="drought-legend">
      <LegendDot color={DROUGHT_RED} label="Drought year (JJAS < 75 % climatology)" />
      <LegendDot color={SAGE_DARK} label="Normal" />
      <LegendDot color={WET_BLUE} label="Wet (JJAS > 125 %)" />
    </div>
  );
}

function LegendDot({ color, label }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: color, border: '1.5px solid #FFFFFF', boxShadow: '0 0 0 1px ' + color }} />
      <span className="font-body" style={{ fontSize: 11, color: '#6F6A63' }}>{label}</span>
    </span>
  );
}
